import type { AuctisyncSupabase } from './client.js';
import { fetchMyStandingsForLots, isMissingRelationError } from './lots.js';

/**
 * The bidder dashboard's derivation — which of my lots am I winning, losing,
 * watching, or have I won.
 *
 * SHARED because it is a set of RULES, not a layout. "Winning" means I lead
 * the lot AND it is still open; "won" means the lot is sold and I am its
 * crowned winner. Those definitions decide what a bidder believes about
 * their own money, and they were previously typed out inline inside a
 * 975-line web page — the single most likely thing in this product to drift
 * between the app and the website while both looked correct.
 *
 * Pure: no client, no fetching. Callers bring the rows.
 */

/**
 * The fields the dashboard rules actually read. Deliberately minimal.
 *
 * 🔴 BOOLEANS, NEVER A BIDDER ID (2026-10-08). The rules used to compare
 * `lots.current_bidder_user_id` / `winning_user_id` with the viewer — columns
 * a signed-in bidder has not been allowed to read since 2026-09-23, so every
 * read that selected them failed for every bidder. `fetchMyBidLots` now fills
 * these from `get_my_standings_for_lots`, which decides "me" server-side.
 */
export interface DashboardLot {
  id: string;
  status: string;
  ends_at: string | null;
  /** I am the high bidder (open) or was at the close. */
  i_lead?: boolean;
  /** The lot SOLD and I am its crowned winner. */
  i_won?: boolean;
}

/**
 * Lot statuses where a bid is still in play.
 *
 * `scheduled` is included deliberately — a lot can carry a preview bid before
 * it opens, and a bidder who placed one should see it rather than wonder where
 * it went.
 */
export const OPEN_FOR_STANDINGS = ['active', 'scheduled', 'closing'] as const;

function isOpen(status: string): boolean {
  return (OPEN_FOR_STANDINGS as readonly string[]).includes(status);
}

export interface BidderDashboard<T extends DashboardLot> {
  /** Open lots where this bidder currently leads. */
  winning: T[];
  /** Open lots this bidder has bid on and does NOT lead. */
  outbid: T[];
  /** Lots on the watchlist, whatever their state. */
  watching: T[];
  /** Sold lots this bidder won. */
  won: T[];
  /** Winning + watching, closing within the window, de-duplicated. */
  endingSoon: T[];
}

export interface BuildDashboardInput<T extends DashboardLot> {
  /** Every lot this bidder has bid on. */
  bidLots: T[];
  /** Every lot on their watchlist. */
  watchedLots: T[];
  nowMs: number;
  /** How far ahead "ending soon" reaches. Defaults to 24 hours. */
  windowMs?: number;
  /**
   * The bidder's LIVE standing on a lot, where something live knows it — on a
   * sale the auction engine runs, `standingFromYou(feed.you(lotId))`
   * (2026-10-08). It decides "leads or not" in place of `i_lead`, which the
   * database answers a beat behind the engine.
   * On a SOLD lot it decides "won" in place of `i_won` the same way.
   * Undefined or null defers to the database; the open-lot rule applies either way.
   */
  liveStanding?: ((lot: T) => 'winning' | 'outbid' | null | undefined) | undefined;
}

export function buildBidderDashboard<T extends DashboardLot>({
  bidLots,
  watchedLots,
  nowMs,
  windowMs = 24 * 3600 * 1000,
  liveStanding,
}: BuildDashboardInput<T>): BidderDashboard<T> {
  /** What the live standing says, or null when it says nothing. */
  const liveSays = (l: T): 'winning' | 'outbid' | null => {
    const live = liveStanding?.(l);
    return live === 'winning' || live === 'outbid' ? live : null;
  };
  const leads = (l: T): boolean => {
    const live = liveSays(l);
    return live !== null ? live === 'winning' : l.i_lead === true;
  };
  const winning = bidLots.filter((l) => leads(l) && isOpen(l.status));
  // NOT simply "everything that is not winning": a lot must have been bid on to
  // count as outbid, which is why this filters bidLots rather than all lots.
  const outbid = bidLots.filter((l) => !leads(l) && isOpen(l.status));
  // Leading when the lot SOLD is winning it: the live standing says so the
  // moment the hammer falls, before the database has crowned anyone.
  const won = bidLots.filter((l) => {
    if (l.status !== 'sold') return false;
    const live = liveSays(l);
    return live !== null ? live === 'winning' : l.i_won === true;
  });

  const cutoff = nowMs + windowMs;
  const soon = [...winning, ...watchedLots].filter(
    (l) => l.ends_at !== null && Date.parse(l.ends_at) <= cutoff,
  );
  // De-duplicated because a lot can be BOTH won-so-far and watched, and showing
  // it twice in one list reads as a bug in the count beside it.
  const endingSoon = [...new Map(soon.map((l) => [l.id, l])).values()].sort((a, b) =>
    (a.ends_at ?? '').localeCompare(b.ends_at ?? ''),
  );

  return { winning, outbid, watching: watchedLots, won, endingSoon };
}

/**
 * Every lot this bidder has bid on, de-duplicated, newest bid first.
 *
 * Goes through `bids` rather than any lot-side column because that is the only
 * record of participation: a lot does not know who has bid on it beyond whoever
 * currently leads, so a bidder who was outbid an hour ago would vanish from
 * their own dashboard.
 *
 * RLS confines this to the caller's OWN bids (`bids_self_select`), which is why
 * it is safe to read directly from the client — a rival's activity is invisible
 * here by construction rather than by our filtering.
 *
 * On a sale the auction engine runs (2026-10-08) a lot's bids are written when
 * it closes, so while it is open the lot is listed from `bidder_lot_positions`
 * (RLS: own rows).
 *
 * Every lot comes back with `i_lead` / `i_won` from `get_my_standings_for_lots`
 * — the server decides "me", and on an engine sale's open lot it takes the
 * engine's answer (20261008223000). `columns` must not name
 * `current_bidder_user_id` or `winning_user_id`: a bidder cannot read them.
 */
export async function fetchMyBidLots<T extends DashboardLot>(
  supabase: AuctisyncSupabase,
  userId: string,
  columns: string,
): Promise<{ lots: T[]; error: string | null }> {
  // Two records, because a lot is in one of them at a time (2026-10-08): on a
  // sale the auction ENGINE runs, a lot's bids reach `bids` when it closes;
  // while it is open, the engine's answer is in `bidder_lot_positions`.
  const [bidsRes, positionsRes] = await Promise.all([
    supabase
      .from('bids')
      .select(`lot_id, created_at, lots:lots(${columns})`)
      .eq('user_id', userId)
      .order('created_at', { ascending: false }),
    supabase
      .from('bidder_lot_positions')
      .select(`lot_id, updated_at, lots:lots(${columns})`)
      .eq('user_id', userId),
  ]);

  if (bidsRes.error) return { lots: [], error: bidsRes.error.message };
  // A database without the table yet answers as "no positions" (see
  // `fetchMyEnginePositions`); any other failure is reported, not guessed.
  const positionsMissing =
    positionsRes.error !== null && isMissingRelationError(positionsRes.error);
  if (positionsRes.error && !positionsMissing) {
    return { lots: [], error: positionsRes.error.message };
  }

  // One lot per bidder, however many times they bid on it. Without this a lot
  // someone bid on six times appears six times, and every count beside it is
  // six times too large. Newest activity first: a bid's time, or the engine's
  // latest word on the lot.
  const byId = new Map<string, { lot: T; at: string }>();
  for (const row of (bidsRes.data ?? []) as unknown as {
    created_at?: string;
    lots: T | null;
  }[]) {
    const lot = row.lots;
    if (lot && !byId.has(lot.id)) byId.set(lot.id, { lot, at: row.created_at ?? '' });
  }
  for (const row of (positionsMissing ? [] : (positionsRes.data ?? [])) as unknown as {
    updated_at?: string;
    lots: T | null;
  }[]) {
    const lot = row.lots;
    if (!lot) continue;
    const seen = byId.get(lot.id);
    const at = [seen?.at ?? '', row.updated_at ?? ''].sort().pop() ?? '';
    byId.set(lot.id, { lot: seen?.lot ?? lot, at });
  }

  let standings: Awaited<ReturnType<typeof fetchMyStandingsForLots>>;
  try {
    standings = await fetchMyStandingsForLots(supabase, [...byId.keys()]);
  } catch (e) {
    return { lots: [], error: e instanceof Error ? e.message : 'Could not load your standing' };
  }

  const lots = [...byId.values()]
    .sort((a, b) => b.at.localeCompare(a.at))
    .map(({ lot }) => {
      const standing = standings.get(lot.id);
      return { ...lot, i_lead: standing?.iLead ?? false, i_won: standing?.iWon ?? false };
    });
  return { lots, error: null };
}
